<template>
  <div>
    <b-form-group
      v-if="showAttachments"
      label-size="sm"
    >
      <template #label>
        <div class="app-ctrl">
          {{ label ? label : $t('Webportal.Attachments') }}
          <span v-if="description" v-b-tooltip.hover.nofade="toolTipDelay" :title="description">
            <font-awesome-icon :icon="['fas', 'info-circle']" />
          </span>
        </div>
      </template>
      <b-card v-if="files.length > 0" body-class="p-2">
        <b-table-simple class="mb-0" responsive="md"  small>
          <template v-for="(file, index) in files">
            <b-tr class="mb-0" :key="index">
              <b-td style="width: 1px">
                <a :href="showPreviewLink ? getFileUrl(file) : null" target="_blank">
                  <font-awesome-icon
                    class="app-ctrl"
                    :icon="['fal', getFileIcon(file.name)]"
                    size="2x"
                  />
                </a>
              </b-td>
              <b-td class="col-form-label-sm text-muted align-middle" :class="{ 'deleted-file': file.markedForDeletion }">
                <a :href="showPreviewLink ? getFileUrl(file) : null" target="_blank">
                  {{ file.name }}
                </a>
              </b-td>
              <b-td
                class="col-form-label-sm text-muted text-right text-nowrap align-middle"
                style="width: 1px"
              >
                {{
                  $n(file.size / 1024, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })
                }}
                KB
              </b-td>
              <b-td
                class="col-form-label-sm text-muted text-right text-nowrap align-middle"
                style="width: 1px; min-width: 150px"
              >
                <div v-if="file.modifiedAt || file.createdAt || file.hasSucceded" class="text-center text-nowrap">
                  <font-awesome-icon :icon="['fal', 'check-circle']" v-if="file.hasSucceded" class="text-success" />
                  {{
                    (file.modifiedAt ? file.modifiedAt : file.createdAt)
                      | msm_datetime_long
                  }}
                </div>
                <div v-else-if="file.isBusy" class="text-center">
                  <b-spinner small variant="primary"></b-spinner>
                </div>
                <div v-else-if="file.hasFailed" class="text-center text-danger text-truncate" style="max-width: 250px;">
                  <span :title="file.error" v-b-tooltip.hover.nofade="toolTipDelay">
                    <font-awesome-icon :icon="['fal', 'exclamation-circle']" />
                    {{ $t("Webportal.UploadFailed") }}<span v-if="file.error">: {{ file.error }}</span>
                  </span>
                </div>
              </b-td>
              <b-td
                class="col-form-label-sm text-muted text-right text-nowrap align-middle"
                style="width: 1px"
              >
                <b-link
                  v-if="!isBusy && isFileDeletable(file)"
                  class="text-danger float-right"
                  :title="file.markedForDeletion ? $t('Webportal.Restore') : $t('Webportal.Delete')"
                  v-b-tooltip.hover.nofade="toolTipDelay"
                  :disabled="disabled"
                  @click="deleteFile(file, index)"
                >
                  <font-awesome-icon :icon="['fas', 'recycle']" v-if="file.markedForDeletion" />
                  <font-awesome-icon :icon="['fas', 'trash-alt']" v-else />
                </b-link>
                <b-link
                  v-if="!file.nativeFile && !isBusy && showDownloadButton"
                  class="float-right"
                  :title="$t('Webportal.Download')"
                  v-b-tooltip.hover.nofade="toolTipDelay"
                  @click="downloadAttachment(file)"
                >
                  <font-awesome-icon :icon="['fas', 'download']" />
                </b-link>
              </b-td>
            </b-tr>
            <b-tr v-if="file.errors || file.warnings" :key="`${index}_messages`">
              <b-td :colspan="5">
                <template v-if="file.errors">
                  <p v-for="(error, errorIndex) in file.errors" :key="`${index}_error_${errorIndex}`" class="text-danger mb-0">
                    <small><font-awesome-icon :icon="['fal', 'exclamation-circle']" /> {{ error.code }} - {{ error.message }}</small>
                  </p>
                </template>
                <template v-if="file.warnings">
                  <p v-for="(warning, warningIndex) in file.warnings" :key="`${index}_warning_${warningIndex}`" class="text-warning mb-0">
                    <small><font-awesome-icon :icon="['fal', 'exclamation-triangle']" /> {{ warning.code }} - {{ warning.message }}</small>
                  </p>
                </template>
              </b-td>
            </b-tr>
          </template>
        </b-table-simple>
      </b-card>
    </b-form-group>

    <b-form-file
      v-if="!readonly"
      ref="fileUpload"
      plain
      hidden
      :multiple="multiple"
      :accept="accept"
      variant="secondary"
      size="sm"
      :disabled="disabled"
      @input="fileUploadInput"
    />

    <b-form-group v-if="showUploadForm">
      <b-button
        v-if="!readonly"
        variant="secondary"
        size="sm"
        @click="selectFile()"
        :disabled="isBusy || disabled"
      >
        <font-awesome-icon :icon="['fas', 'folder-open']" />
        {{ $t("Webportal.SelectFile") }}
      </b-button>
      <b-button
        v-if="files.length > 0"
        variant="secondary"
        size="sm"
        class="float-right"
        style="width: 100px"
        :disabled="!uploadUrl || isBusy || !changePending || disabled"
        @click="uploadFiles()"
      >
        {{ $t("Webportal.Send") }} <b-spinner v-show="isBusy" small></b-spinner>
      </b-button>
    </b-form-group>
  </div>
</template>

<script>
import AppConfig from "@/mixins/AppConfig-APP_TARGET";
import DownloadFileMixin from "@/mixins/DownloadFileMixin";
import axios from "axios";

export default {
  name: "FileUploader",

  mixins: [AppConfig, DownloadFileMixin],

  props: {
    value: { type: Array, default: () => [] },
    label: { type: String, default: null },
    description: { type: String, default: null },
    uploadUrl: { type: String, default: null },
    uploadUrlParams: { type: [Object, Function], default: null },
    additionalFormData: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    multiple: { type: Boolean, default: true },
    accept: { type: String, default: "*" },
    fileRenameCallback: { type: Function, default: null },
    fileDeletableCallback: { type: Function, default: null },
    showPreviewLink: { type: Boolean, default: true },
    showDownloadButton: { type: Boolean, default: true },
    continueOnError: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false }
  },

  data() {
    return {
      files: this.value,
      isBusy: false,
    };
  },

  watch: {
    value(newVal, oldVal) {
      if (newVal) {
        // Don't loose file uploads added by user
        for (const file of this.files.filter(f => f.nativeFile)) {
          if (!newVal.find(f => f.nativeFile === file.nativeFile))
            newVal.push(file);
        };

        this.files = newVal;
      }
    },

    isBusy(newVal, oldVal) {
      this.$emit('is-busy-changed', newVal);
    },
  },

  computed: {
    showAttachments() {
      return !this.readonly || this.files?.length > 0;
    },

    changePending() {
      return this.files.some(f => f.markedForDeletion) || this.files.some(f => f.nativeFile);
    },

    showUploadForm() {
      return !this.readonly || !this.uploadUrl || this.changePending;
    },
  },

  methods: {
    getFileUrl(file) {
      return this.uploadUrl && file.id ? this.uploadUrl + "/" + file.id : null;
    },

    selectFile() {
      this.$refs.fileUpload.$el.click();
    },

    fileUploadInput(files) {
      if (!files) return;

      const checkFile = (f) => {
        if (this.fileRenameCallback instanceof Function) {
          f = this.renameFile(f, this.fileRenameCallback(f));
        }

        const found = this.findFile(f);
        if (found) {
          found.nativeFile = f;
          found.size = f.size;
          return;
        }

        this.files.push({ nativeFile: f, name: f.name, size: f.size });
      };

      if (Array.isArray(files))
        files.forEach(checkFile);
      else
        checkFile(files);

      this.$refs.fileUpload.reset();
    },

    findFile(file) {
      return this.files.find(f => f.nativeFile && f.nativeFile.name == file.name);
    },

    isFileDeletable(file) {
      if (this.fileDeletableCallback instanceof Function)
        return this.fileDeletableCallback(file);
      return !!file?.nativeFile;
    },

    deleteFile(file, index) {
      let removed = true, fromServer = false;

      if (file.nativeFile) {
        // Remove items, which are queued for upload
        this.files.splice(index, 1);
      }
      else {
        // (Un-)Queue uploaded items for deletion
        fromServer = true;
        removed = !file.markedForDeletion;
        this.$set(this.files, index, { ...file, markedForDeletion: removed });
      }

      this.$emit("file-removed", file, fromServer, removed);
    },

    renameFile(origFile, newName) {
      if (!(origFile instanceof File) || typeof newName !== "string")
        return origFile;

      return new File([origFile], newName, {
          type: origFile.type,
          lastModified: origFile.lastModified
      });
    },

    async downloadAttachment(file) {
      this.downloadFile(
        await axios.get(this.getFileUrl(file), {
          responseType: "blob",
        })
      );
    },

    async uploadFiles() {
      try {
        this.isBusy = true;

        // Delete
        for (const file of this.files.filter((f) => f.markedForDeletion)) {
          try {
            await axios.delete(this.getFileUrl(file),
              {
                params: this.uploadUrlParams instanceof Function ?
                  this.uploadUrlParams(file) :
                  this.uploadUrlParams
              });
  
            // delete existing file entry
            const index = this.files.findIndex(
              (f) => f.name == file.name && f.markedForDeletion
            );
            if (index > -1) this.files.splice(index, 1);
          } catch (error) {
            file.hasFailed = true;
            throw error;
          } finally {
            file.isBusy = false;
          }
        }

        // Upload
        for (const file of this.files.filter((f) => f.nativeFile)) {
          file.isBusy = true;

          let formData = new FormData();
          formData.append("file", file.nativeFile);

          if (this.additionalFormData)
            this.additionalFormData.forEach(element => {
              let [name, value] = element;
              formData.append(name, value);
            });

          try {
            delete file.hasFailed;
            delete file.error;
            delete file.errors;
            delete file.warnings;

            this.$forceUpdate();

            let response = await axios.post(this.uploadUrl, formData, {
              headers: {
                "Content-Type": "text/plain",
              },
              params: this.uploadUrlParams instanceof Function ?
                this.uploadUrlParams(file) :
                this.uploadUrlParams,
            });

            file.id = response.data.id;
            file.modifiedAt = response.data.modifiedAt;
            file.warnings = response.data.warnings;
            file.hasSucceded = true;

            // remove existing file entry
            let index = this.files.findIndex(
              (f) => f.name == file.name && !f.nativeFile
            );
            if (index > -1) this.files.splice(index, 1);

            file.nativeFile = null;
          } catch (error) {
            file.hasFailed = true;
            file.error = error.response.data?.error;
            file.errors = error.response.data?.errors;
            file.warnings = error.response.data?.warnings;
            
            if (this.continueOnError == false)
              throw error;
          } finally {
            file.isBusy = false;
          }
        }
      } catch (error) {
        console.error(error);

        this.$bvToast.toast(
          this.$t("Webportal.SaveFailed.Text", {
            entity: this.$t("Webportal.Attachments"),
          }),
          {
            autoHideDelay: 10 * 1000,
            variant: "danger",
            noCloseButton: true,
          }
        );
      } finally {
        this.isBusy = false;
      }
    },
  },
};
</script>
<style scoped lang="scss">
.deleted-file {
  font-style: italic;
  text-decoration: line-through;
}

.text-muted {
  background-color: var(--background-color-primary)!important;
  color: var(--color-primary)!important;

  a:not(.text-danger) {
    background-color: var(--background-color-primary)!important;
    border-color: var(--color-accent);
    color: var(--color-primary)!important;
  }
}
</style>